import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { toast } from 'sonner';

export interface CameraOptions {
  quality?: number;
  source?: 'camera' | 'photos' | 'prompt';
}

export interface CameraResult {
  dataUrl: string;
  format: string;
}

export function useCamera() {
  const [isCapturing, setIsCapturing] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  /**
   * Captures a raw photo without any editing.
   * The image should be cropped using ImageCropModal after capture.
   */
  const capturePhoto = async (options: CameraOptions = {}): Promise<CameraResult | null> => {
    const {
      quality = 90,
      source = 'prompt',
    } = options;

    setIsCapturing(true);

    try {
      if (isNative) {
        // Use Capacitor Camera on native platforms
        const cameraSource = 
          source === 'camera' ? CameraSource.Camera :
          source === 'photos' ? CameraSource.Photos :
          CameraSource.Prompt;

        const photo = await Camera.getPhoto({
          quality,
          allowEditing: false, // Disabled - we use our own crop modal
          resultType: CameraResultType.DataUrl,
          source: cameraSource,
          correctOrientation: true,
          // Portuguese translations for camera prompts
          promptLabelHeader: 'Enviar imagem',
          promptLabelPhoto: 'Da galeria',
          promptLabelPicture: 'Tirar uma foto',
        });

        if (photo.dataUrl) {
          return {
            dataUrl: photo.dataUrl,
            format: photo.format || 'jpeg',
          };
        }

        return null;
      } else {
        // Web fallback - use file input
        return new Promise((resolve) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.capture = source === 'camera' ? 'environment' : undefined;

          input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
              resolve(null);
              return;
            }

            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result as string;
              // Return raw image - cropping will be done by ImageCropModal
              resolve({
                dataUrl,
                format: 'jpeg',
              });
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          };

          input.oncancel = () => resolve(null);
          input.click();
        });
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      
      // Handle user cancellation gracefully
      if (error instanceof Error && error.message.includes('User cancelled')) {
        // User cancelled, no toast needed
        return null;
      }
      
      toast.error('Erro ao capturar foto. Verifique as permissões da câmera.');
      return null;
    } finally {
      setIsCapturing(false);
    }
  };

  return {
    capturePhoto,
    isCapturing,
    isNative,
  };
}

// Utility function to convert data URL to File
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}
